import {
  type AssociationsV2,
  FuscaIdentificationTokenRequester,
  GraphQLIdentificationTokenRequester,
  IdentificationTokenRequester,
  type TokenToSave,
  type TokenValidationV2,
} from '@cobliteam/api-lib';
import HttpStatusCode from '@cobliteam/api-lib/src/request/HttpStatusCode';

import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

const QUERY_V2_KEY = 'IDENTIFICATION_TOKEN_V2';
const QUERY_GET_IDENTIFICATIONS_TOKENS_V2_KEY = 'QUERY_GET_IDENTIFICATIONS_TOKENS_V2_KEY';

const graphQLRequester = () => new GraphQLIdentificationTokenRequester();

const useIdentificationToken = () => {
  const env = Environment.getInstance().getEnvironment();
  const requester = new IdentificationTokenRequester({
    host: env.herbieApiHost,
  });
  const fuscaRequester = new FuscaIdentificationTokenRequester({
    host: env.apiHost,
  });

  const queryClient = useQueryClient();

  const useTokenAssociationsV2 = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<AssociationsV2>({
      queryKey: [QUERY_V2_KEY],
      queryFn: () => fuscaRequester.getTokenAssociationsV2(),
      retry: false,
      enabled: true,
      refetchOnMount: false,
    });
  };

  const useTokenValidationV2 = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<TokenValidationV2, unknown, string>({
      mutationFn: requester.tokenValidationV2,
    });
  };

  const useCreateNewTokens = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<void, AxiosError, TokenToSave[]>({
      mutationFn: async (newTokens) => fuscaRequester.createTokens(newTokens),
      onSuccess: () =>
        queryClient.resetQueries({
          queryKey: [QUERY_GET_IDENTIFICATIONS_TOKENS_V2_KEY],
        }),
      onError: (error: AxiosError) => {
        if (error.request?.status === HttpStatusCode.CONFLICT) {
          Alert.error('Falha ao criar identificadores: um ou mais já existem.');
          return;
        }

        Alert.error('Falha ao criar identificadores. Tente novamente mais tarde.');
      },
    });
  };

  // Consider using useIdentifierAssociations, since it's using FuscaAPI
  const useDeleteToken = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<void, unknown, string>({
      mutationFn: async (id) => graphQLRequester().deleteToken(id),
      onSuccess: () => {
        Alert.success('Identificador deletado com sucesso.');
      },
      onError: () => {
        Alert.error('Falha ao deletar identificador. Tente novamente mais tarde.');
      },
    });
  };

  return {
    useTokenAssociationsV2,
    useTokenValidationV2,
    useCreateNewTokens,
    useDeleteToken,
    QUERY_V2_KEY,
  };
};

export default useIdentificationToken;
